<ng-container *ngIf="!(isPartOfArNavigation$ | async)">
    <div class="content-wrapper">
        <div class="alert alert-danger">
            {{ 'aumico_reports_page_is_not_part_of_ar_navigation' | i18next }}
        </div>
    </div>
</ng-container>

<ng-container *ngIf="isPartOfArNavigation$ | async" content>
    <div class="content-wrapper" cdkDropListGroup>
        <!-- REV-20899: Reclassification conflicts banner -->
        <div
            *ngIf="reclassConflictCount > 0 && !reclassConflictsViewOpen"
            class="reclass-conflict-banner"
            (click)="toggleReclassConflictsView()"
        >
            <span class="reclass-conflict-icon" aria-hidden="true">⚠</span>
            <span class="reclass-conflict-text">
                {{ 'aumico_grouping_masking_reclass_conflicts_banner' | i18next : { count: reclassConflictCount } }}
            </span>
            <span class="reclass-conflict-action"
                >{{ 'aumico_grouping_masking_reclass_conflicts_show' | i18next }} ›</span
            >
        </div>

        <!-- Legacy report with existing reclasses (edited in the old tab): the new
             reclass feature is disabled here; the user keeps editing reclasses in the
             existing "Umgliederungen & Stille Reserven" tab. Reuses the shared
             warning banner component (orange, exclamation icon). -->
        <app-update-banner
            *ngIf="reclassFeatureDisabled"
            style="margin-bottom: 16px"
            messageLocalizationKey="aumico_grouping_masking_reclass_legacy_banner"
        >
        </app-update-banner>

        <!-- REV-20899: Conflict resolution view (replaces the table when open) -->
        <aumico-reclassification-conflicts
            *ngIf="reclassConflictsViewOpen"
            [transactions]="reclassTransactions"
            [accountsByStatement]="getReclassAccountsByStatement()"
            (deleteByAccount)="onReclassConflictDelete($event)"
            (reassignAccount)="onReclassConflictReassign($event)"
            (backToTable)="toggleReclassConflictsView()"
        ></aumico-reclassification-conflicts>

        <div class="navigation-header" [hidden]="reclassConflictsViewOpen">
            <ham-tab-navigation
                [navigationItems]="navigationItems"
                [tabsTitles]="tabsTitles"
                [style]="'underline'"
                [color]="'primary'"
                (tabClick)="onTabClick($event)"
            ></ham-tab-navigation>

            <div class="action-buttons">
                <ham-button
                    *ngIf="!openAllFoldersWasClicked"
                    variant="white"
                    outline="true"
                    (click)="onOpenAllFolders()"
                >
                    {{ 'aumico_reports_equality_check_print_preview_show_details' | i18next }}
                </ham-button>
                <ham-button
                    *ngIf="openAllFoldersWasClicked"
                    variant="white"
                    outline="true"
                    (click)="onCloseAllFolders()"
                >
                    {{ 'aumico_reports_equality_check_print_preview_hide_details' | i18next }}
                </ham-button>

                <!-- REV-20899: Umgliederungsspiegel preview button -->
                <ham-button
                    *ngIf="reclassTransactions.length > 0"
                    variant="white"
                    outline="true"
                    (click)="onOpenReclassPreview()"
                >
                    {{ 'aumico_grouping_masking_reclass_preview_button' | i18next }}
                </ham-button>

                <div class="sort-button-container">
                    <ham-button
                        variant="white"
                        outline="true"
                        rightIcon="assets/hamIcons/chevron-down.svg"
                        (click)="onSortButtonClick($event)"
                    >
                        {{ 'aumico_grouping_masking_sort_account_btn' | i18next }}
                    </ham-button>
                    <div
                        class="menu-flyout"
                        hamFlyout
                        *ngIf="isSortMenuOpen"
                        (closeFlyout)="closeSortMenu()"
                        [position]="sortMenuPosition"
                    >
                        <ul>
                            <li (click)="sortAccounts('alphabetical')">
                                <span>{{ 'aumico_grouping_masking_sort_account_name_based' | i18next }}</span>
                            </li>
                            <li (click)="sortAccounts('accountNumber')">
                                <span>{{ 'aumico_grouping_masking_sort_account_number_based' | i18next }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <!-- REV-20899: hide the tabs/table while the conflicts view is open
             so the user only sees the conflict resolution panel. The
             navigation header above is already hidden via the same flag. -->
        <div
            *ngFor="let tab of navigationItems; let i = index"
            [hidden]="selectedTab !== i || reclassConflictsViewOpen"
        >
            <div class="tab-content">
                <ng-container [ngSwitch]="i">
                    <div *ngSwitchCase="0">
                        <ng-container
                            *ngTemplateOutlet="
                                financialStatementTemplate;
                                context: {
                                    data: balanceSheet,
                                    dueDateCY: dueDateCY,
                                    dueDatePY: dueDatePY,
                                    currency: currency,
                                    prefix: 'bs'
                                }
                            "
                        ></ng-container>
                    </div>

                    <div *ngSwitchCase="1">
                        <ng-container
                            *ngTemplateOutlet="
                                incomeStatementTemplate;
                                context: {
                                    data: incomeStatement,
                                    dueDateCY: periodCY,
                                    dueDatePY: periodPY,
                                    currency: currency,
                                    prefix: 'is'
                                }
                            "
                        ></ng-container>
                    </div>
                </ng-container>

                <!-- MAIN FINANCIAL STATEMENT TEMPLATE -->
                <ng-template
                    #financialStatementTemplate
                    let-data="data"
                    let-prefix="prefix"
                    let-dueDateCY="dueDateCY"
                    let-dueDatePY="dueDatePY"
                    let-currency="currency"
                >
                    <div class="table-header-row">
                        <div class="header-label">{{ currency }}</div>
                        <div class="header-label text-end">{{ dueDateCY | dateLocale }}</div>
                        <div class="header-label text-end reclass-header">
                            {{ 'aumico_grouping_masking_reclass_column_header_cy' | i18next }}
                        </div>
                        <div class="header-label text-end">{{ dueDatePY | dateLocale }}</div>
                        <div class="header-label text-end reclass-header reclass-header-py">
                            {{ 'aumico_grouping_masking_reclass_column_header_py' | i18next }}
                            <!-- REV-21132: lock toggle switch makes PY reclass cells
                                 read-only. State is shown three ways — switch position,
                                 track color, and the text label — so locked vs. editable
                                 is unmistakable. Persisted on `IGroupingMasking.reclassPyLocked`. -->
                            <button
                                type="button"
                                role="switch"
                                class="reclass-py-lock-toggle"
                                [class.locked]="reclassPyLocked"
                                [attr.aria-checked]="reclassPyLocked"
                                [disabled]="reclassFeatureDisabled"
                                [title]="
                                    (reclassPyLocked
                                        ? 'aumico_grouping_masking_reclass_py_unlock_tooltip'
                                        : 'aumico_grouping_masking_reclass_py_lock_tooltip'
                                    ) | i18next
                                "
                                (click)="toggleReclassPyLock()"
                            >
                                <span class="reclass-py-lock-track" aria-hidden="true">
                                    <span class="reclass-py-lock-thumb"></span>
                                </span>
                                <span class="reclass-py-lock-label">
                                    <span class="reclass-py-lock-glyph" aria-hidden="true">{{
                                        reclassPyLocked ? '🔒' : '🔓'
                                    }}</span>
                                    {{
                                        (reclassPyLocked
                                            ? 'aumico_grouping_masking_reclass_py_lock_state_locked'
                                            : 'aumico_grouping_masking_reclass_py_lock_state_editable'
                                        ) | i18next
                                    }}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div class="table-body">
                        <ng-container
                            *ngTemplateOutlet="
                                recursiveGroupNode;
                                context: {
                                    items: data?.sortedItems,
                                    parentPath: prefix,
                                    level: 0,
                                    mainGroupIndex: null,
                                    prefix: prefix
                                }
                            "
                        ></ng-container>
                    </div>
                </ng-template>

                <!-- MAIN INCOME  STATEMENT TEMPLATE -->
                <ng-template
                    #incomeStatementTemplate
                    let-data="data"
                    let-prefix="prefix"
                    let-dueDateCY="dueDateCY"
                    let-dueDatePY="dueDatePY"
                    let-currency="currency"
                >
                    <div class="table-header-row">
                        <div class="header-label">{{ currency }}</div>
                        <div class="header-label text-end" [innerHTML]="dueDateCY | dateLocale"></div>
                        <div class="header-label text-end reclass-header">
                            {{ 'aumico_grouping_masking_reclass_column_header_cy' | i18next }}
                        </div>
                        <div class="header-label text-end" [innerHTML]="dueDatePY | dateLocale"></div>
                        <div class="header-label text-end reclass-header reclass-header-py">
                            {{ 'aumico_grouping_masking_reclass_column_header_py' | i18next }}
                            <!-- REV-21132: lock toggle switch makes PY reclass cells
                                 read-only. State is shown three ways — switch position,
                                 track color, and the text label — so locked vs. editable
                                 is unmistakable. Persisted on `IGroupingMasking.reclassPyLocked`. -->
                            <button
                                type="button"
                                role="switch"
                                class="reclass-py-lock-toggle"
                                [class.locked]="reclassPyLocked"
                                [attr.aria-checked]="reclassPyLocked"
                                [disabled]="reclassFeatureDisabled"
                                [title]="
                                    (reclassPyLocked
                                        ? 'aumico_grouping_masking_reclass_py_unlock_tooltip'
                                        : 'aumico_grouping_masking_reclass_py_lock_tooltip'
                                    ) | i18next
                                "
                                (click)="toggleReclassPyLock()"
                            >
                                <span class="reclass-py-lock-track" aria-hidden="true">
                                    <span class="reclass-py-lock-thumb"></span>
                                </span>
                                <span class="reclass-py-lock-label">
                                    <span class="reclass-py-lock-glyph" aria-hidden="true">{{
                                        reclassPyLocked ? '🔒' : '🔓'
                                    }}</span>
                                    {{
                                        (reclassPyLocked
                                            ? 'aumico_grouping_masking_reclass_py_lock_state_locked'
                                            : 'aumico_grouping_masking_reclass_py_lock_state_editable'
                                        ) | i18next
                                    }}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div class="table-body">
                        <div class="section-wrapper section-wrapper-level-0">
                            <!-- Section Header for income statement -->
                            <div class="grid-row section-header-row" [style.--level]="0">
                                <div class="col-name">
                                    <span class="title">{{ data?.title }}</span>
                                </div>
                                <div class="col-val col-cy">
                                    {{ finalCY(data) | number : '1.2-2' }}
                                </div>
                                <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                <div class="col-val col-py">
                                    {{ finalPY(data) | number : '1.2-2' }}
                                </div>
                                <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                            </div>
                            <div class="nested-children">
                                <ng-container
                                    *ngTemplateOutlet="
                                        recursiveGroupNode;
                                        context: {
                                            items: data?.sortedItems,
                                            parentPath: prefix,
                                            level: 1,
                                            mainGroupIndex: null,
                                            prefix: prefix
                                        }
                                    "
                                ></ng-container>
                            </div>
                        </div>
                    </div>
                </ng-template>

                <!-- RECURSIVE GROUP NODE TEMPLATE -->
                <ng-template
                    #recursiveGroupNode
                    let-items="items"
                    let-parentPath="parentPath"
                    let-level="level"
                    let-mainGroupIndex="mainGroupIndex"
                    let-prefix="prefix"
                >
                    <ng-container *ngFor="let item of items; index as groupIndex; trackBy: trackByGroup">
                        <ng-container *ngIf="isGroup(item)">
                            <ng-container *ngIf="hasGroupContent(item)">
                                <ng-container *ngIf="getGroupPath(parentPath, item) as groupPath">
                                    <div class="section-wrapper" [class.section-wrapper-level-0]="level === 0">
                                        <!-- Section Header eg Assets or Liabilities -->
                                        <div
                                            class="grid-row"
                                            [ngClass]="level === 0 ? 'section-header-row' : 'group-row'"
                                            [style.--level]="level"
                                            (click)="toggleCollapse(groupPath)"
                                        >
                                            <div class="col-name">
                                                <span
                                                    *ngIf="level > 0"
                                                    class="ham-icon chevron"
                                                    [class.expanded]="!isCollapsed(groupPath)"
                                                >
                                                    <i class="chevron-right-icon"></i>
                                                </span>
                                                <span class="title">{{ item.title }}</span>
                                            </div>
                                            <div class="col-val col-cy">
                                                {{ finalCY(item) | number : '1.2-2' }}
                                            </div>
                                            <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                            <div class="col-val col-py">
                                                {{ finalPY(item) | number : '1.2-2' }}
                                            </div>
                                            <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                                        </div>

                                        <div class="nested-children" *ngIf="!isCollapsed(groupPath)">
                                            <ng-container
                                                *ngTemplateOutlet="
                                                    recursiveGroupNode;
                                                    context: {
                                                        items: item.sortedItems,
                                                        parentPath: groupPath,
                                                        level: level + 1,
                                                        mainGroupIndex: level === 0 ? groupIndex : mainGroupIndex,
                                                        prefix: prefix
                                                    }
                                                "
                                            ></ng-container>
                                        </div>
                                    </div>
                                </ng-container>
                            </ng-container>
                        </ng-container>

                        <ng-container *ngIf="!isGroup(item)">
                            <ng-container
                                *ngTemplateOutlet="
                                    leafItem;
                                    context: {
                                        item: item,
                                        parentPath: parentPath,
                                        level: level,
                                        mainGroupIndex: level === 0 ? groupIndex : mainGroupIndex,
                                        prefix: prefix
                                    }
                                "
                            ></ng-container>
                        </ng-container>
                    </ng-container>
                </ng-template>

                <!-- LEAF ITEM TEMPLATE (FLIs, Headers, Subtotals) -->
                <ng-template
                    #leafItem
                    let-item="item"
                    let-parentPath="parentPath"
                    let-level="level"
                    let-mainGroupIndex="mainGroupIndex"
                    let-prefix="prefix"
                >
                    <ng-container [ngSwitch]="item.type">
                        <ng-container *ngSwitchCase="ItemType.FLI">
                            <ng-container *ngIf="hasFliContent(item)">
                                <ng-container *ngIf="getFliPath(parentPath, item) as fliPath">
                                    <!-- FLI Header -->
                                    <div
                                        class="grid-row fli-row"
                                        [style.--level]="level"
                                        (click)="toggleCollapse(fliPath)"
                                    >
                                        <div class="col-name">
                                            <span class="ham-icon chevron" [class.expanded]="!isCollapsed(fliPath)">
                                                <i class="chevron-right-icon"></i>
                                            </span>
                                            {{ item.caption || item.name }}
                                        </div>
                                        <div class="col-val col-cy">
                                            {{ finalCY(item) | number : '1.2-2' }}
                                        </div>
                                        <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                        <div class="col-val col-py">
                                            {{ finalPY(item) | number : '1.2-2' }}
                                        </div>
                                        <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                                    </div>

                                    <!-- FLI Content: Uses Shared #accountListTemplate.
                                         REV-20899: phantom reclass accounts (created via the
                                         nested new-account modal) are stored on `fli.reclassesAccounts`
                                         and rendered alongside the regular imported accounts so they
                                         show up under their target FLI in the same table. The merge
                                         happens in `fliRowEntries(item)`. -->
                                    <ng-container *ngIf="!isCollapsed(fliPath)">
                                        <ng-container
                                            *ngTemplateOutlet="
                                                accountListTemplate;
                                                context: {
                                                    accounts: fliRowEntries(item),
                                                    currentPath: fliPath,
                                                    wrapperClass: 'drop-list-container',
                                                    mainGroupIndex: mainGroupIndex,
                                                    prefix: prefix,
                                                    level: level + 1
                                                }
                                            "
                                        ></ng-container>
                                    </ng-container>
                                </ng-container>
                            </ng-container>
                        </ng-container>

                        <ng-container *ngSwitchCase="ItemType.HEADER">
                            <!-- Title Row -->
                            <div class="grid-row title-row" [style.--level]="level">
                                <div class="col-name">{{ item.caption || item.name }}</div>
                                <div class="col-val col-cy">&nbsp;</div>
                                <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                <div class="col-val col-py">&nbsp;</div>
                                <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                            </div>
                        </ng-container>
                        <ng-container *ngSwitchCase="ItemType.EMPTY">
                            <!-- Empty Row -->
                            <div class="grid-row title-row" [style.--level]="level">
                                <div class="col-name">&nbsp;</div>
                                <div class="col-val col-cy">&nbsp;</div>
                                <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                <div class="col-val col-py">&nbsp;</div>
                                <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                            </div>
                        </ng-container>
                        <ng-container *ngSwitchCase="ItemType.SUB_TOTAL">
                            <ng-container *ngIf="getSubtotalPath(parentPath, item) as subtotalPath">
                                <!-- Subtotal row -->
                                <div
                                    class="grid-row subtotal-row"
                                    [style.--level]="level"
                                    [class.hidden-row]="isSubtotalHidden(subtotalPath)"
                                >
                                    <div class="col-name">
                                        <!-- Checkbox for subtotal selection -->
                                        <input
                                            type="checkbox"
                                            class="row-checkbox"
                                            [checked]="selectedSubtotals.has(subtotalPath)"
                                            (change)="onToggleCheckSubtotal(item, subtotalPath)"
                                        />
                                        {{ item.caption || item.name }}
                                    </div>
                                    <div class="col-val col-cy">
                                        <span>
                                            {{ finalCY(item) | number : '1.2-2' }}
                                        </span>
                                    </div>
                                    <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                    <div class="col-val col-py">
                                        <span>
                                            {{ finalPY(item) | number : '1.2-2' }}
                                        </span>
                                    </div>
                                    <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                                </div>
                            </ng-container>
                        </ng-container>
                    </ng-container>
                </ng-template>

                <!-- SUB-FLI TEMPLATE -->
                <ng-template
                    #subFliTemplate
                    let-acc="acc"
                    let-fliPath="fliPath"
                    let-mainGroupIndex="mainGroupIndex"
                    let-prefix="prefix"
                    let-level="level"
                >
                    <ng-container *ngIf="acc.accounts && acc.accounts.length > 0">
                        <ng-container *ngIf="getSubFliPath(fliPath, acc) as subFliPath">
                            <!-- SUB FLI HEADER -->
                            <div
                                class="grid-row fli-row sub-fli"
                                [style.--level]="level"
                                (click)="toggleCollapse(subFliPath)"
                            >
                                <div class="col-name">
                                    <span class="ham-icon chevron" [class.expanded]="!isCollapsed(subFliPath)">
                                        <i class="chevron-right-icon"></i>
                                    </span>
                                    <span class="drag-handle" cdkDragHandle></span>

                                    {{ acc.caption || acc.name }}
                                </div>
                                <div class="col-val">
                                    {{ finalCY(acc) | number : '1.2-2' }}
                                </div>
                                <div class="col-val reclass-col-aggregate">&nbsp;</div>
                                <div class="col-val">
                                    {{ finalPY(acc) | number : '1.2-2' }}
                                </div>
                                <div class="col-val reclass-col-aggregate">&nbsp;</div>
                            </div>

                            <!-- SUB FLI Content: Uses Shared #accountListTemplate -->
                            <ng-container *ngIf="!isCollapsed(subFliPath)">
                                <ng-container
                                    *ngTemplateOutlet="
                                        accountListTemplate;
                                        context: {
                                            accounts: acc.accounts,
                                            currentPath: subFliPath,
                                            wrapperClass: 'drop-list-container sub-fli-drop-list sub-fli-account-list',
                                            mainGroupIndex: mainGroupIndex,
                                            prefix: prefix,
                                            level: level + 1
                                        }
                                    "
                                ></ng-container>
                            </ng-container>
                        </ng-container>
                    </ng-container>
                </ng-template>

                <!-- OF_THAT_ITEM TEMPLATE (Similar to SUB-FLI but with 'davon' prefix) -->
                <ng-template
                    #ofThatItemTemplate
                    let-acc="acc"
                    let-fliPath="fliPath"
                    let-mainGroupIndex="mainGroupIndex"
                    let-prefix="prefix"
                    let-level="level"
                >
                    <ng-container *ngIf="acc.accounts && acc.accounts.length > 0">
                        <ng-container *ngIf="getSubFliPath(fliPath, acc) as subFliPath">
                            <!-- OF_THAT_ITEM HEADER -->
                            <div
                                class="grid-row fli-row sub-fli"
                                [style.--level]="level"
                                (click)="toggleCollapse(subFliPath)"
                            >
                                <div class="col-name">
                                    <span class="ham-icon chevron" [class.expanded]="!isCollapsed(subFliPath)">
                                        <i class="chevron-right-icon"></i>
                                    </span>
                                    <span class="drag-handle" cdkDragHandle></span>
                                    <span
                                        ><span class="acc-of-that-item-prefix">
                                            {{ aumico_templates_group_account_mapping_of_that_item_text }}
                                        </span>
                                        {{ acc.caption || acc.name }}</span
                                    >
                                </div>
                                <div class="col-val">
                                    {{ finalCY(acc) | number : '1.2-2' }}
                                </div>
                                <div class="col-val reclass-col-aggregate">&nbsp;</div>
                                <div class="col-val">
                                    {{ finalPY(acc) | number : '1.2-2' }}
                                </div>
                                <div class="col-val reclass-col-aggregate">&nbsp;</div>
                            </div>

                            <!-- Content: Uses Shared #accountListTemplate -->
                            <ng-container *ngIf="!isCollapsed(subFliPath)">
                                <ng-container
                                    *ngTemplateOutlet="
                                        accountListTemplate;
                                        context: {
                                            accounts: acc.accounts,
                                            currentPath: subFliPath,
                                            wrapperClass: 'drop-list-container sub-fli-drop-list sub-fli-account-list',
                                            mainGroupIndex: mainGroupIndex,
                                            prefix: prefix,
                                            level: level + 1
                                        }
                                    "
                                ></ng-container>
                            </ng-container>
                        </ng-container>
                    </ng-container>
                </ng-template>

                <!-- 
                    SHARED ACCOUNT LIST TEMPLATE 
                    Handles: Groups, Split Accounts, Single Accounts, Nested Sub-FLIs, and Of-That-Items
                    Note: cdkDropList MUST be inside here for CDK to see the cdkDrag items.
                -->
                <ng-template
                    #accountListTemplate
                    let-accounts="accounts"
                    let-currentPath="currentPath"
                    let-wrapperClass="wrapperClass"
                    let-mainGroupIndex="mainGroupIndex"
                    let-prefix="prefix"
                    let-level="level"
                >
                    <div
                        [class]="wrapperClass"
                        cdkDropList
                        [id]="currentPath"
                        [cdkDropListData]="accounts || []"
                        [cdkDropListConnectedTo]="getConnectedDropLists(currentPath)"
                        [cdkDropListEnterPredicate]="createDropPredicate(currentPath)"
                        (cdkDropListDropped)="drop($event, accounts, currentPath)"
                    >
                        <ng-container *ngFor="let acc of accounts || []; trackBy: trackByAccount">
                            <!-- 1a. Handle Nested Sub-FLIs -->
                            <!-- REV-21342: SubFLI rows are STRUCTURE (defined in Account Mapping) and must not
                                 be manually sortable here — their drag is disabled. -->
                            <ng-container *ngIf="acc.type === 'SUB FLI'">
                                <ng-container *ngIf="getSubFliPath(currentPath, acc) as subFliPath">
                                    <div
                                        cdkDrag
                                        [cdkDragDisabled]="true"
                                        [cdkDragData]="acc"
                                        (cdkDragStarted)="onGroupDragStarted(subFliPath)"
                                        (cdkDragEnded)="onGroupDragEnded(subFliPath)"
                                    >
                                        <div class="cdk-drag-placeholder" *cdkDragPlaceholder></div>
                                        <ng-container
                                            *ngTemplateOutlet="
                                                subFliTemplate;
                                                context: {
                                                    acc: acc,
                                                    fliPath: currentPath,
                                                    mainGroupIndex: mainGroupIndex,
                                                    prefix: prefix,
                                                    level: level
                                                }
                                            "
                                        ></ng-container>
                                    </div>
                                </ng-container>
                            </ng-container>

                            <!-- 1b. Handle Of-That Items -->
                            <!-- REV-21342: structural rows (like SubFLIs) — manual sorting disabled. -->
                            <ng-container *ngIf="acc.type === 'OF_THAT_ITEM'">
                                <ng-container *ngIf="getSubFliPath(currentPath, acc) as ofThatPath">
                                    <div
                                        cdkDrag
                                        [cdkDragDisabled]="true"
                                        [cdkDragData]="acc"
                                        (cdkDragStarted)="onGroupDragStarted(ofThatPath)"
                                        (cdkDragEnded)="onGroupDragEnded(ofThatPath)"
                                    >
                                        <div class="cdk-drag-placeholder" *cdkDragPlaceholder></div>
                                        <ng-container
                                            *ngTemplateOutlet="
                                                ofThatItemTemplate;
                                                context: {
                                                    acc: acc,
                                                    fliPath: currentPath,
                                                    mainGroupIndex: mainGroupIndex,
                                                    prefix: prefix,
                                                    level: level
                                                }
                                            "
                                        ></ng-container>
                                    </div>
                                </ng-container>
                            </ng-container>

                            <!-- 2. Handle Grouped Accounts -->
                            <div
                                class="group-account-container"
                                *ngIf="
                                    acc.groupId &&
                                    acc.accounts?.length > 0 &&
                                    acc.type !== 'SUB FLI' &&
                                    acc.type !== 'OF_THAT_ITEM' &&
                                    acc.type !== 'HEADER'
                                "
                                [class.splitAccount]="acc.isGroupedSplitAccount"
                                [class.splitAccountCounterpart]="acc.isGroupedSplitAccountCounterpart"
                            >
                                <div
                                    class="grid-row account-row group-summary-row"
                                    [class.expanded]="!isCollapsed(acc.groupId)"
                                    [style.--level]="level"
                                    cdkDrag
                                    [cdkDragData]="acc"
                                    (cdkDragStarted)="onGroupDragStarted(acc.groupId)"
                                    (cdkDragEnded)="onGroupDragEnded(acc.groupId)"
                                >
                                    <div class="cdk-drag-placeholder" *cdkDragPlaceholder></div>

                                    <div class="col-name">
                                        <span
                                            class="ham-icon chevron"
                                            style="cursor: pointer"
                                            [class.expanded]="!isCollapsed(acc.groupId)"
                                            (click)="toggleCollapse(acc.groupId); $event.stopPropagation()"
                                        >
                                            <i class="chevron-right-icon"></i>
                                        </span>
                                        <span class="drag-handle" cdkDragHandle></span>

                                        <span class="acc-name"
                                            >{{ acc.groupName }}
                                            <span *ngIf="acc.isGroupedSplitAccountCounterpart" class="acc-copy">
                                                ({{ 'aumico_grouping_masking_splitted' | i18next }})</span
                                            >
                                        </span>

                                        <div class="action-icons">
                                            <span
                                                hamIconShapes
                                                icon="assets/hamIcons/edit-outline.svg"
                                                [size]="'20px'"
                                                [variant]="'transparent-teal-400'"
                                                (click)="onEditGroup(acc.groupId)"
                                                [hamTooltip]="'aumico_grouping_masking_edit_group_name' | i18next"
                                                [withArrow]="true"
                                                [backGroundHexColor]="'#1F2A37'"
                                                *ngIf="!acc.isGroupedSplitAccountCounterpart"
                                            ></span>
                                            <span
                                                hamIconShapes
                                                icon="assets/hamIcons/transaction.svg"
                                                [size]="'20px'"
                                                [variant]="
                                                    !acc.isGroupedSplitAccount
                                                        ? 'transparent-teal-400'
                                                        : 'teal-400-transparent'
                                                "
                                                (click)="onDefineBalanceSwitchGroup(acc, mainGroupIndex)"
                                                [hamTooltip]="
                                                    !acc.isGroupedSplitAccount
                                                        ? aumico_grouping_masking_define_bsa
                                                        : aumico_grouping_masking_remove_bsa
                                                "
                                                [withArrow]="true"
                                                [backGroundHexColor]="'#1F2A37'"
                                                *ngIf="!acc.isGroupedSplitAccountCounterpart && prefix === 'bs'"
                                            ></span>
                                            <span
                                                hamIconShapes
                                                icon="assets/hamIcons/minus-outline.svg"
                                                [size]="'20px'"
                                                [variant]="'transparent-teal-400'"
                                                (click)="onRemoveSplitAccount(acc)"
                                                [hamTooltip]="aumico_grouping_masking_remove_bsa"
                                                [withArrow]="true"
                                                [backGroundHexColor]="'#1F2A37'"
                                                *ngIf="acc.isGroupedSplitAccountCounterpart && prefix === 'bs'"
                                            ></span>
                                            <span
                                                hamIconShapes
                                                icon="assets/hamIcons/duplicate-folders.svg"
                                                [size]="'20px'"
                                                [variant]="'teal-400-transparent'"
                                                (click)="onUngroupAccounts(acc.groupId)"
                                                [hamTooltip]="aumico_grouping_masking_ungroup"
                                                [withArrow]="true"
                                                [backGroundHexColor]="'#1F2A37'"
                                                *ngIf="!acc.isGroupedSplitAccountCounterpart"
                                            ></span>
                                        </div>
                                    </div>

                                    <div class="col-val font-weight-bold col-cy">
                                        {{ finalCY(acc) | number : '1.2-2' }}
                                    </div>
                                    <!-- Reclasses are applied at the individual account level only;
                                         the group summary row shows no reclass cell. -->
                                    <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                    <div class="col-val font-weight-bold col-py">
                                        {{ finalPY(acc) | number : '1.2-2' }}
                                    </div>
                                    <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                                </div>

                                <div
                                    class="grouped-account-children"
                                    *ngIf="!isCollapsed(acc.groupId)"
                                    cdkDropList
                                    [id]="acc.groupId"
                                    [cdkDropListData]="acc.accounts"
                                    [cdkDropListConnectedTo]="getConnectedDropLists(acc.groupId)"
                                    [cdkDropListEnterPredicate]="canDropIntoGroup"
                                    (cdkDropListDropped)="dropIntoGroup($event, acc)"
                                >
                                    <!-- Accounts inside Grouped Account -->
                                    <div
                                        class="grid-row account-row child-row"
                                        *ngFor="let childAcc of acc.accounts"
                                        [style.--level]="level + 1"
                                    >
                                        <div class="col-name">
                                            <span class="acc-number">{{ displayAccountNumberFor(childAcc) }}</span>
                                            <span class="acc-name"
                                                ><span>
                                                    {{
                                                        getAccountDisplayName(
                                                            childAcc.accountNumber,
                                                            childAcc.name,
                                                            childAcc.shareholder,
                                                            childAcc.uniqueAccountId
                                                        )
                                                    }}
                                                </span
                                                ><span
                                                    *ngIf="isReclassAccountRow(childAcc)"
                                                    class="reclass-account-badge"
                                                    hamIconShapes
                                                    icon="assets/hamIcons/turn_right.svg"
                                                    [size]="'16px'"
                                                    [variant]="'teal-400-transparent'"
                                                    [hamTooltip]="reclassAccountBadgeTooltip"
                                                    [withArrow]="true"
                                                    [backGroundHexColor]="'#1F2A37'"
                                                ></span>
                                                <span
                                                    *ngIf="!acc.isGroupedSplitAccountCounterpart"
                                                    hamIconShapes
                                                    icon="assets/hamIcons/minus-outline.svg"
                                                    [size]="'20px'"
                                                    [variant]="'transparent-teal-400'"
                                                    (click)="onRemoveAccountFromGroup(childAcc, acc)"
                                                    [hamTooltip]="aumico_grouping_masking_remove_from_group"
                                                    [withArrow]="true"
                                                    [backGroundHexColor]="'#1F2A37'"
                                                ></span
                                            ></span>
                                        </div>
                                        <div class="col-val col-cy">
                                            {{
                                                ((acc.isGroupedSplitAccount || acc.isGroupedSplitAccountCounterpart) &&
                                                acc.calculatedClosingBalance == 0
                                                    ? 0
                                                    : finalCY(childAcc)
                                                ) | number : '1.2-2'
                                            }}
                                        </div>
                                        <div
                                            class="col-val col-cy reclass-col-cell"
                                            *ngIf="canReclassAccount(childAcc); else childReclassCyBlank"
                                        >
                                            <aumico-reclassification-cell
                                                [accountRef]="reclassRefForAccount(childAcc)"
                                                [period]="ReclassificationPeriod.CURRENT_YEAR"
                                                [statement]="currentReclassStatement"
                                                [rowNo]="childAcc?.rowNo"
                                                [value]="
                                                    reclassSumForAccount(childAcc, ReclassificationPeriod.CURRENT_YEAR)
                                                "
                                                [disabled]="reclassFeatureDisabled"
                                                (cellClick)="onReclassCellClick($event)"
                                            ></aumico-reclassification-cell>
                                        </div>
                                        <div class="col-val col-py">
                                            {{
                                                ((acc.isGroupedSplitAccount || acc.isGroupedSplitAccountCounterpart) &&
                                                acc.calculatedOpeningBalance == 0
                                                    ? 0
                                                    : finalPY(childAcc)
                                                ) | number : '1.2-2'
                                            }}
                                        </div>
                                        <div
                                            class="col-val col-py reclass-col-cell"
                                            *ngIf="canReclassAccount(childAcc); else childReclassPyBlank"
                                        >
                                            <aumico-reclassification-cell
                                                [accountRef]="reclassRefForAccount(childAcc)"
                                                [period]="ReclassificationPeriod.PREVIOUS_YEAR"
                                                [statement]="currentReclassStatement"
                                                [rowNo]="childAcc?.rowNo"
                                                [value]="
                                                    reclassSumForAccount(childAcc, ReclassificationPeriod.PREVIOUS_YEAR)
                                                "
                                                [disabled]="reclassFeatureDisabled || reclassPyLocked"
                                                (cellClick)="onReclassCellClick($event)"
                                            ></aumico-reclassification-cell>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 3a. Handle Header Items -->
                            <ng-container *ngIf="acc.type === 'HEADER'">
                                <div class="grid-row account-row" [style.--level]="level" cdkDrag [cdkDragData]="acc">
                                    <div class="col-name">
                                        <span class="drag-handle withCheckboxMargin" cdkDragHandle></span>
                                        <span class="acc-name font-weight-bold">
                                            {{ acc.caption }}
                                        </span>
                                    </div>
                                    <div class="col-val col-cy">&nbsp;</div>
                                    <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                    <div class="col-val col-py">&nbsp;</div>
                                    <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                                </div>
                            </ng-container>
                            <!-- 3a. Handle Header Items -->
                            <ng-container *ngIf="acc.type === 'EMPTY'">
                                <div class="grid-row account-row" [style.--level]="level" cdkDrag [cdkDragData]="acc">
                                    <div class="col-name">
                                        <span class="drag-handle withCheckboxMargin" cdkDragHandle></span>
                                        <span class="acc-number"></span>
                                        <span class="acc-name"> </span>
                                    </div>
                                    <div class="col-val col-cy">&nbsp;</div>
                                    <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
                                    <div class="col-val col-py">&nbsp;</div>
                                    <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
                                </div>
                            </ng-container>

                            <!-- 4. Handle Standard Accounts -->
                            <ng-container
                                *ngIf="
                                    !acc.groupId &&
                                    acc.type !== 'SUB FLI' &&
                                    acc.type !== 'OF_THAT_ITEM' &&
                                    acc.type !== 'HEADER' &&
                                    acc.type !== 'EMPTY'
                                "
                            >
                                <div class="grid-row account-row" [style.--level]="level" cdkDrag [cdkDragData]="acc">
                                    <div class="cdk-drag-placeholder" *cdkDragPlaceholder></div>
                                    <div class="col-name">
                                        <input
                                            type="checkbox"
                                            class="row-checkbox"
                                            [checked]="isAccountSelected(acc)"
                                            (click)="onToggleCheckAccountClick(acc, accounts, $event)"
                                            (change)="onToggleCheckAccount(acc)"
                                        />
                                        <span class="drag-handle" cdkDragHandle></span>
                                        <span class="acc-number">{{ displayAccountNumberFor(acc) }}</span>
                                        <span
                                            class="acc-name"
                                            [class.masked]="
                                                isAccountMasked(acc.accountNumber, acc.name, acc.shareholder, acc.uniqueAccountId)
                                            "
                                        >
                                            {{
                                                getAccountDisplayName(
                                                    acc.accountNumber,
                                                    acc.name,
                                                    acc.shareholder,
                                                    acc.uniqueAccountId
                                                )
                                            }}

                                            <!-- REV-21798: marks a row the user created through a
                                                 reclassification. Placed AFTER the name so the row
                                                 reads "MAN <name> ⊕" like every other account row;
                                                 `circle-plus` keeps it distinct from the
                                                 balance-switch (transaction) and renamed (pencil)
                                                 markers. -->
                                            <span
                                                *ngIf="isReclassAccountRow(acc)"
                                                class="reclass-account-badge"
                                                hamIconShapes
                                                icon="assets/hamIcons/turn_right.svg"
                                                [size]="'16px'"
                                                [variant]="'teal-400-transparent'"
                                                [hamTooltip]="reclassAccountBadgeTooltip"
                                                [withArrow]="true"
                                                [backGroundHexColor]="'#1F2A37'"
                                            ></span>

                                            <span
                                                *ngIf="
                                                    isAccountMasked(
                                                        acc.accountNumber,
                                                        acc.name,
                                                        acc.shareholder,
                                                        acc.uniqueAccountId
                                                    )
                                                "
                                                hamIconShapes
                                                icon="assets/hamIcons/edit-outline.svg"
                                                [size]="'20px'"
                                                [variant]="'teal-400-transparent'"
                                                (click)="onChangeRenaming(acc)"
                                                [hamTooltip]="aumico_grouping_masking_remove_original_name(acc.name)"
                                                [withArrow]="true"
                                                [backGroundHexColor]="'#1F2A37'"
                                            ></span>
                                        </span>
                                    </div>
                                    <div class="col-val col-cy">
                                        {{ finalCY(acc) | number : '1.2-2' }}
                                    </div>
                                    <div
                                        class="col-val col-cy reclass-col-cell"
                                        *ngIf="canReclassAccount(acc); else accReclassCyBlank"
                                    >
                                        <aumico-reclassification-cell
                                            [accountRef]="reclassRefForAccount(acc)"
                                            [period]="ReclassificationPeriod.CURRENT_YEAR"
                                            [statement]="currentReclassStatement"
                                            [rowNo]="acc?.rowNo"
                                            [value]="reclassSumForAccount(acc, ReclassificationPeriod.CURRENT_YEAR)"
                                            [disabled]="reclassFeatureDisabled"
                                            (cellClick)="onReclassCellClick($event)"
                                        ></aumico-reclassification-cell>
                                    </div>
                                    <div class="col-val col-py">
                                        {{ finalPY(acc) | number : '1.2-2' }}
                                    </div>
                                    <div
                                        class="col-val col-py reclass-col-cell"
                                        *ngIf="canReclassAccount(acc); else accReclassPyBlank"
                                    >
                                        <aumico-reclassification-cell
                                            [accountRef]="reclassRefForAccount(acc)"
                                            [period]="ReclassificationPeriod.PREVIOUS_YEAR"
                                            [statement]="currentReclassStatement"
                                            [rowNo]="acc?.rowNo"
                                            [value]="reclassSumForAccount(acc, ReclassificationPeriod.PREVIOUS_YEAR)"
                                            [disabled]="reclassFeatureDisabled || reclassPyLocked"
                                            (cellClick)="onReclassCellClick($event)"
                                        ></aumico-reclassification-cell>
                                    </div>
                                </div>
                            </ng-container>
                        </ng-container>
                    </div>
                </ng-template>
            </div>
        </div>
    </div>
</ng-container>

<ng-template #footerTemplate>
    <aum-action-bar-pure
        [extraActionButtons]="footerExtraButtonActions"
        nextButtonPrefixText="{{ nextStepPrefix | i18next }}"
        nextButtonText="{{ nextStepLabel | i18next }}"
        [nextButtonAction]="proceedButtonClicked"
        nextButtonRouterLink="../../{{ nextStepPath }}"
        [nextButtonDisabled]="disableProceedButton"
        backButtonPrefixText="{{ previousStepPrefix | i18next }}"
        backButtonText="{{ uploadStepLabel | i18next }}"
        backButtonRouterLink="../{{ uploadStepPath }}"
        [backButtonIcon]="previousStepIcon"
    ></aum-action-bar-pure>
</ng-template>

<bulk-edit-action-bar
    *ngIf="selectedAccounts?.size > 0 || selectedSubtotals?.size > 0"
    [selectedCount]="selectedAccounts?.size + selectedSubtotals?.size"
    [actions]="bulkActions"
    [deleteAction]="bulkDeleteAction"
    textSelectedTranslationKey="{{
        selectedAccounts?.size
            ? 'aumico_grouping_masking_bulk_action_accounts_selected'
            : 'aumico_grouping_masking_bulk_action_sub_total_selected'
    }}"
    [globalPosition]="true"
    (actionClicked)="onActionClicked($event)"
></bulk-edit-action-bar>

<!-- Rename Modal -->
<ham-modal
    *ngIf="openRenameModal"
    [title]="aumico_grouping_masking_rename_modal_title"
    [saveText]="aumico_grouping_masking_modal_apply"
    [showCancelButton]="true"
    [cancelText]="aumico_grouping_masking_modal_cancel"
    (save)="onRenameSave()"
    (cancel)="openRenameModal = false"
>
    <p class="modal-description" style="text-align: left">
        {{ aumico_grouping_masking_rename_modal_text }}
    </p>
    <form [formGroup]="renameForm" class="mask-form">
        <div class="mask-form-header">
            <label>{{ aumico_grouping_masking_rename_modal_label_original }}</label>
            <label>{{ aumico_grouping_masking_rename_modal_label_new }}</label>
        </div>
        <div *ngFor="let account of accountsToRename" class="mask-form-field">
            <div class="original-account-info">
                <span class="account-number">{{ account.accountNumber }}</span>
                <span class="account-name">{{
                    getAccountDisplayNameWithoutMasking(account.accountNumber, account.name, account.shareholder)
                }}</span>
            </div>
            <!-- FIX: Use component helper getIdentifier -->
            <input
                hamInput
                [formControlName]="getIdentifier(account)"
                placeholder="{{ aumico_grouping_masking_rename_modal_label_placeholder }}"
            />
            <ham-button
                *ngIf="editRenameModal"
                (click)="
                    resetAccountName(
                        account.accountNumber,
                        account.name,
                        account.shareholder,
                        account.uniqueAccountId
                    )
                "
                >{{ aumico_grouping_masking_rename_modal_label_delete }}</ham-button
            >
        </div>
    </form>
</ham-modal>

<!-- Grouping Modal -->
<ham-modal
    *ngIf="openGroupModal"
    [title]="aumico_grouping_masking_grouping_modal_title"
    [saveText]="aumico_grouping_masking_modal_apply"
    [showCancelButton]="true"
    [cancelText]="aumico_grouping_masking_modal_cancel"
    (save)="onGroupSave()"
    (cancel)="openGroupModal = false"
>
    <p class="modal-description" style="text-align: left">{{ aumico_grouping_masking_grouping_modal_text }}</p>
    <form [formGroup]="groupForm" class="mask-form">
        <input
            hamInput
            [formControlName]="'groupName'"
            [placeholder]="aumico_grouping_masking_grouping_modal_label_placeholder"
        />
    </form>
</ham-modal>
<!-- Balance Switch Modal -->
<ham-modal
    *ngIf="openSplitModal"
    [title]="aumico_grouping_masking_split_modal_title"
    [saveText]="aumico_grouping_masking_modal_apply"
    [showCancelButton]="true"
    [cancelText]="aumico_grouping_masking_modal_cancel"
    (save)="onSplitAccountSave()"
    (cancel)="openSplitModal = false"
    [bodyClass]="'split-modal-body'"
    [headerClass]="'split-modal-header'"
>
    <div class="split-modal-content">
        <label class="split-label">{{ aumico_grouping_masking_split_modal_title_start }}</label>
        <div class="split-section">
            <div class="split-row starting-position">
                <span>{{ accountToSplit?.name }}</span>
                <span class="text-end">{{ accountToSplit?.calculatedClosingBalance | number : '1.2-2' }}</span>
                <span class="text-end">{{ accountToSplit?.calculatedOpeningBalance | number : '1.2-2' }}</span>
            </div>
        </div>

        <label class="split-label">{{ aumico_grouping_masking_split_modal_title_output }}</label>
        <div class="split-section">
            <div class="split-row preview-output">
                <span>{{ splitPreview?.activeAccount.name }}</span>
                <span class="text-end">{{
                    splitPreview?.activeAccount.calculatedClosingBalance | number : '1.2-2'
                }}</span>
                <span class="text-end">{{
                    splitPreview?.activeAccount.calculatedOpeningBalance | number : '1.2-2'
                }}</span>
            </div>
            <div class="split-row preview-output">
                <span>{{ splitPreview?.passiveAccount.name }}</span>
                <span class="text-end">{{
                    splitPreview?.passiveAccount.calculatedClosingBalance | number : '1.2-2'
                }}</span>
                <span class="text-end">{{
                    splitPreview?.passiveAccount.calculatedOpeningBalance | number : '1.2-2'
                }}</span>
            </div>
        </div>

        <form [formGroup]="splitForm" class="split-form">
            <div class="form-group">
                <label for="financial-line-item">{{ aumico_grouping_masking_split_modal_title_counterpart }}</label>
                <input
                    id="financial-line-item"
                    hamSimpleDropDown
                    [options]="financialLineItems"
                    [optionKeys]="['name']"
                    returnKey="id"
                    [allowFreeText]="false"
                    formControlName="financialLineItem"
                    [placeholder]="aumico_grouping_masking_split_modal_title_counterpart_placeholder"
                />
            </div>
        </form>
    </div>
</ham-modal>

<ham-confirm-decline-modal
    class="confirm-decline-modal"
    *ngIf="showConfirmDialog"
    (cancel)="onCancelConfirm()"
    (action)="onConfirm()"
    footerAlignment="center"
    [actionBtnText]="confirmActionText"
    [noActionBtnText]="aumico_grouping_masking_modal_dismiss"
>
    <ham-general-confirm-decline-modal-body [text]="confirmText"></ham-general-confirm-decline-modal-body>
</ham-confirm-decline-modal>

<!-- REV-20899: Reclassification modal -->
<aumico-reclassification-modal
    *ngIf="reclassModalOpen && reclassModalContext"
    [class.reclass-modal-behind-nested]="isNestedReclassDialogOpen"
    [sourceRef]="reclassModalContext.sourceRef"
    [sourceLabel]="reclassModalContext.sourceLabel"
    [sourceBalance]="reclassModalContext.sourceBalance"
    [period]="reclassModalContext.period"
    [statement]="reclassModalContext.statement"
    [existingOutgoing]="reclassModalContext.existingOutgoing"
    [existingIncoming]="reclassModalContext.existingIncoming"
    [availableTargets]="reclassModalContext.availableTargets"
    [sourceSide]="reclassModalContext.sourceSide"
    [targetSideResolver]="reclassTargetSideResolver"
    (saveClicked)="onReclassModalSave($event)"
    (cancelClicked)="onReclassModalCancel()"
    (createAccountRequested)="onCreateAccountRequested($event)"
></aumico-reclassification-modal>

<!-- REV-20899: Umgliederungsspiegel preview -->
<aumico-reclassification-preview
    *ngIf="reclassPreviewOpen"
    [transactions]="reclassTransactions"
    [currency]="currency ?? 'CHF'"
    [locale]="annualReport?.locale ?? 'de-CH'"
    (closed)="onCloseReclassPreview()"
></aumico-reclassification-preview>

<!-- The "+" button's first step: create an account, or a group? -->
<ham-modal
    *ngIf="newTargetChoiceOpen"
    class="reclass-nested-modal"
    [style.--reclass-nested-modal-height.px]="nestedReclassDialogHeight"
    [title]="'aumico_grouping_masking_reclass_new_target_choice_title' | i18next"
    [showCancelButton]="true"
    [cancelText]="'aumico_grouping_masking_modal_cancel' | i18next"
    (cancel)="onNewTargetChoiceCancelled()"
>
    <aumico-reclass-new-target-choice (chosen)="onNewTargetChoice($event)"></aumico-reclass-new-target-choice>
</ham-modal>

<!-- REV-20899: Nested new-account modal -->
<aumico-new-account-nested-modal
    *ngIf="newAccountModalOpen && newAccountModalStatement"
    class="reclass-nested-modal"
    [style.--reclass-nested-modal-height.px]="nestedReclassDialogHeight"
    [statement]="newAccountModalStatement"
    [existingAccountNumbers]="reclassExistingAccountNumbers"
    [fliOptions]="reclassNewAccountFliOptions"
    (created)="onNewAccountCreated($event)"
    (cancelled)="onNewAccountCancelled()"
></aumico-new-account-nested-modal>

<!-- Nested new-GROUP modal — the group branch of the same "+" button -->
<aumico-new-group-nested-modal
    *ngIf="newGroupModalOpen && newAccountModalStatement"
    class="reclass-nested-modal"
    [style.--reclass-nested-modal-height.px]="nestedReclassDialogHeight"
    [statement]="newAccountModalStatement"
    [fliOptions]="reclassNewAccountFliOptions"
    [existingGroupNamesByFli]="reclassExistingGroupNamesByFli"
    (created)="onNewGroupCreated($event)"
    (cancelled)="onNewGroupCancelled()"
></aumico-new-group-nested-modal>

<!-- An account with no number (e.g. the transferred "Jahresgewinn /-verlust") cannot be
     reclassified: it is a computed row, not a posting account, and the reclassification engine
     resolves every ref by account number. The cell is replaced by the same inert placeholder the
     EMPTY and group-summary rows use, so the grid keeps its four value columns. -->
<ng-template #childReclassCyBlank>
    <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
</ng-template>
<ng-template #childReclassPyBlank>
    <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
</ng-template>
<ng-template #accReclassCyBlank>
    <div class="col-val col-cy reclass-col-aggregate">&nbsp;</div>
</ng-template>
<ng-template #accReclassPyBlank>
    <div class="col-val col-py reclass-col-aggregate">&nbsp;</div>
</ng-template>
